import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Plus, Check, Layers } from "lucide-react";
import { toast } from "sonner";
import { PlayerForm } from "../../components/app/PlayerForm";
import { useAuth, levelOf } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { api, avatarSrc, toList } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, Empty, Avatar, Btn, height, fmt, firstPosition, PracticeTag, CaptainBadge, StarterTag } from "../../components/app/kit";
import { TeamLeaders } from "../../components/app/TeamLeaders";
import { LeaderBadges } from "../../components/app/LeaderBadges";
import { useLeaders } from "../../hooks/useLeaders";
import { useCWeek, cWeekFor } from "../../hooks/useCWeek";
import { CWeekBadge } from "../../components/app/CWeekBadge";

const POS = ["All", "PG", "SG", "SF", "PF", "C"];

const ViewToggle = ({ value, onChange }) => (
  <div className="no-print inline-flex rounded-full border border-[#1E293B] bg-[#0F172A] p-1" role="tablist">
    {[["players", "Players"], ["leaders", "Leaders"]].map(([k, l]) => (
      <button key={k} role="tab" aria-selected={value === k} data-testid={`roster-view-${k}`} onClick={() => onChange(k)}
        className={`rounded-full px-4 py-2 font-mono-gd text-xs uppercase tracking-wider transition-colors ${value === k ? "bg-[#39FF14] text-[#0A0E1A]" : "text-slate-300 hover:text-white"}`}>{l}</button>
    ))}
  </div>
);

const CARD = "group block w-full rounded-2xl border bg-[#0F172A] p-5 text-left transition-[border-color,transform] duration-300 hover:-translate-y-1";

/** Link to the profile normally; a selectable tile while the coach is setting levels. */
const CardShell = ({ p, selecting, selected, onToggle, children }) => (selecting ? (
  <button type="button" onClick={onToggle} aria-pressed={selected} data-testid={`roster-select-${p.id}`}
    className={`${CARD} relative ${selected ? "border-[#39FF14] ring-2 ring-[#39FF14]/40" : "border-[#1E293B] hover:border-slate-500"}`}>
    <span className={`absolute left-3 top-3 flex h-5 w-5 items-center justify-center rounded-md border ${selected ? "border-[#39FF14] bg-[#39FF14] text-[#0A0E1A]" : "border-slate-500"}`}>{selected && <Check className="h-3.5 w-3.5" />}</span>
    {children}
  </button>
) : (
  <Link to={`/coach/players/${p.id}`} data-testid={`roster-player-card-${p.id}`} className={`${CARD} border-[#1E293B] hover:border-[#39FF14]/60`}>{children}</Link>
));

const LevelBar = ({ count, busy, onSet, onAll, onDone }) => (
  <div data-testid="roster-level-bar" className="no-print sticky top-2 z-20 mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-[#39FF14]/40 bg-[#0A0E1A]/90 p-3 backdrop-blur-md">
    <p data-testid="roster-selected-count" className="mr-auto font-mono-gd text-xs uppercase tracking-wider text-slate-200">{count} selected · tap players to pick</p>
    <Btn variant="ghost" data-testid="roster-select-all" onClick={onAll}>Select all</Btn>
    <Btn data-testid="roster-make-jv" disabled={!count || busy} onClick={() => onSet("jv")}>Make JV</Btn>
    <Btn data-testid="roster-make-varsity" disabled={!count || busy} onClick={() => onSet("varsity")}>Make Varsity</Btn>
    <Btn variant="ghost" data-testid="roster-levels-done" onClick={onDone}>Done</Btn>
  </div>
);

const Leaders = ({ tid, ids }) => {
  const stats = useApi(tid && `/teams/${tid}/stats/team`);
  if (stats.loading) return <Loader />;
  if (stats.error) return <ErrorNote error={stats.error} />;
  const rows = toList(stats.data, ["players"]).filter((r) => ids.has(r.player_id ?? r.id));
  return <div data-testid="roster-leaders"><TeamLeaders rows={rows} /></div>;
};

export default function Roster() {
  const { membership, canEdit, level, inLevel } = useAuth();
  const tid = membership?.team_id;
  const { data, loading, error, reload } = useApi(tid && `/teams/${tid}/players`);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("All");
  const [practiceOnly, setPracticeOnly] = useState(false);
  const [view, setView] = useState("players");
  const [selecting, setSelecting] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const done = () => { setSelecting(false); setSel(new Set()); };
  const setLevels = async (lv) => {
    const ids = [...sel];
    setBusy(true);
    const res = await Promise.allSettled(ids.map((id) => api.patch(`/teams/${tid}/players/${id}`, { level: lv })));
    const failed = res.filter((r) => r.status === "rejected").length;
    setBusy(false);
    if (failed) toast.error(`${failed} player${failed === 1 ? "" : "s"} couldn't be updated`);
    if (failed < ids.length) toast.success(`${ids.length - failed} moved to ${lv === "jv" ? "JV" : "Varsity"}`);
    setSel(new Set());
    reload();
  };
  const all = useMemo(() => toList(data, ["players"]), [data]);
  const badges = useLeaders(tid, all);
  const cw = useCWeek(tid);
  const levelIds = useMemo(() => new Set(toList(data, ["players"]).filter(inLevel).map((p) => p.id)), [data, inLevel]);
  const players = useMemo(() => toList(data, ["players"]).filter((p) => {
    const okQ = !q || p.name?.toLowerCase().includes(q.toLowerCase()) || String(p.jersey ?? "").includes(q);
    const okP = pos === "All" || firstPosition(p) === pos;
    return okQ && okP && inLevel(p) && (!practiceOnly || p.practice_player);
  }), [data, q, pos, inLevel, practiceOnly]);
  const practiceCount = useMemo(() => all.filter((p) => p.practice_player && inLevel(p)).length, [all, inLevel]);

  return (
    <div>
      <PageHeader eyebrow={`${membership?.team_name ?? ""}${level === "all" ? "" : ` · ${level === "jv" ? "JV" : "Varsity"}`}`} title="Roster">
        <ViewToggle value={view} onChange={setView} />
        {canEdit && view === "players" && !selecting && <Btn variant="ghost" data-testid="roster-set-levels" onClick={() => setSelecting(true)}><Layers className="h-4 w-4" />Set JV / Varsity</Btn>}
        {canEdit && <Btn data-testid="add-player-button" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />Add player</Btn>}
      </PageHeader>
      {canEdit && <PlayerForm open={adding} onClose={() => setAdding(false)} teamId={tid} onSaved={reload} />}
      {view === "leaders" ? (loading ? <Loader /> : error ? <ErrorNote error={error} /> : <Leaders tid={tid} ids={levelIds} />) : (<>
      {selecting && <LevelBar count={sel.size} busy={busy} onSet={setLevels} onAll={() => setSel(new Set(players.map((p) => p.id)))} onDone={done} />}
      <div className="no-print mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <label className="flex flex-1 items-center gap-2 rounded-full border border-[#1E293B] bg-[#0F172A] px-4 py-2.5 focus-within:border-[#39FF14]">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid="roster-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or jersey" className="w-full bg-transparent text-sm text-white outline-none placeholder:text-slate-500" />
        </label>
        <div className="flex gap-1 overflow-x-auto">
          {POS.map((p) => (
            <button key={p} data-testid={`roster-filter-${p.toLowerCase()}`} onClick={() => setPos(p)} className={`rounded-full px-3.5 py-2 font-mono-gd text-xs ${pos === p ? "bg-[#39FF14] text-[#0A0E1A]" : "bg-[#162032] text-slate-300"}`}>{p}</button>
          ))}
          <button data-testid="roster-filter-practice" aria-pressed={practiceOnly} onClick={() => setPracticeOnly((v) => !v)} className={`shrink-0 rounded-full border px-3.5 py-2 font-mono-gd text-xs uppercase transition-colors ${practiceOnly ? "border-amber-300 bg-amber-300 text-[#0A0E1A]" : "border-amber-300/40 text-amber-200 hover:bg-amber-300/10"}`}>Practice squad {practiceCount}</button>
        </div>
      </div>
      {loading ? <Loader /> : error ? <ErrorNote error={error} /> : !players.length ? <Empty title={practiceOnly ? "No practice players" : level === "all" ? "No players found" : `No ${level === "jv" ? "JV" : "Varsity"} players found`} text={practiceOnly ? (canEdit ? "Turn on Practice player in a player's Edit form to add them here." : undefined) : canEdit && !q && pos === "All" ? (level === "all" ? "Add your first player to start building the roster." : "Switch the toggle to All and use Set JV / Varsity to move players here.") : undefined} /> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {players.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.4) }}>
              <CardShell p={p} selecting={selecting} selected={sel.has(p.id)} onToggle={() => toggle(p.id)}>
                <div className="flex items-center gap-4">
                  <Avatar name={p.name} src={avatarSrc(tid, p)} />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 truncate font-display text-2xl font-bold uppercase leading-tight text-white"><span className="truncate">{p.name}</span>{p.captain && <CaptainBadge testid={`roster-captain-${p.id}`} />}</p>
                    <p className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">
                      <span data-testid={`roster-level-${p.id}`} className={levelOf(p) === "jv" ? "text-[#00E5FF]" : "text-[#39FF14]"}>{levelOf(p) === "jv" ? "JV" : "Varsity"}</span> · <span data-testid={`roster-position-${p.id}`}>{firstPosition(p)}</span> · Class of {p.grad_year ?? "—"}
                    </p>
                    {(p.practice_player || p.starter) && <div className="mt-1 flex flex-wrap gap-1.5">{p.starter && <StarterTag testid={`roster-starter-${p.id}`} />}{p.practice_player && <PracticeTag testid={`roster-practice-${p.id}`} />}</div>}
                    {p.primary_archetype && <p className="truncate text-xs text-slate-300">{p.primary_archetype}{p.secondary_archetype ? ` / ${p.secondary_archetype}` : ""}</p>}
                  </div>
                  <span className="font-display text-4xl font-black text-[#1E293B] transition-colors group-hover:text-[#39FF14]">#{p.jersey ?? "–"}</span>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2 border-t border-[#1E293B] pt-4 font-mono-gd text-xs">
                  {(canEdit
                    ? [["HT", height(p.height_in)], ["GPA", fmt(p.gpa)], ["INT", fmt(p.interest_count ?? 0)], ["TOP", fmt(p.top_target_count ?? 0)]]
                    : [["HT", height(p.height_in)], ["WT", p.weight_lb ? `${p.weight_lb}` : "—"], ["POS", firstPosition(p)], ["CLASS", p.grad_year ?? "—"]]
                  ).map(([l, v]) => (
                    <div key={l}><p className="text-slate-400">{l}</p><p className={`text-base ${l === "TOP" && v !== "0" ? "text-[#39FF14]" : "text-white"}`}>{v}</p></div>
                  ))}
                </div>
                {badges[p.id] && <div className="mt-3"><LeaderBadges badges={badges[p.id]} testid={`roster-leader-badges-${p.id}`} /></div>}
                {cWeekFor(cw, p.id) && <div className="mt-2"><CWeekBadge cw={cWeekFor(cw, p.id)} testid={`roster-c-week-${p.id}`} /></div>}
              </CardShell>
            </motion.div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
